/** Season's Home for a brand-new account: setup in four steps, then the first room, one next step at a time. */
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { HomeResponse } from "../../../shared/types";
import { BOARD_NEW_PATH, boardPath } from "../../board/paths";
import { DockedBar } from "../../components/season/DockedBar";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { GroupHead, PageHead } from "../../components/season/PageHead";
import { Icon } from "../../components/ui/Icon";
import { SETUP_STEPS, nextSetupPath, setupCount } from "../../setup/SetupContext";
import { clock, longDay } from "../../lib/when";

export function SeasonFresh({ layout, data, name, date, menu }: { layout: SeasonLayout; data: HomeResponse; name: string; date: string; menu: ReactNode }) {
  const done = setupCount(data.setup);
  const room = data.first_room;
  // Why one main action: setup first, then the first room; both done leaves nothing to push.
  const next = done < 4 ? { label: "Start setup", to: nextSetupPath(data.setup) } : room ? null : { label: "Make your first room", to: BOARD_NEW_PATH };
  const makeIsNext = next?.to === BOARD_NEW_PATH;
  const phone = layout === "phone";
  const when = room ? (room.live ? (room.starts_at ? `Live, ${longDay(room.starts_at)}, ${clock(room.starts_at)}` : "A live room") : "A recording people watch any time") : "";

  return (
    <Page layout={layout} narrow className="hs">
      <PageHead eyebrow={date} title={name ? `Welcome, ${name}` : "Welcome"} right={menu} />
      <GroupHead title="Set up Flashroom" count={`${done} of 4 done`} />
      <GroupedList>
        {SETUP_STEPS.map((s, i) => (
          <ListRow
            key={s.key}
            lead={
              <span className={data.setup[s.key] ? "k-ico good" : i === done ? "k-ico wine" : "k-ico"}>{data.setup[s.key] ? <Icon name="check" /> : <b>{i + 1}</b>}</span>
            }
            title={s.label}
            to={s.path}
          />
        ))}
      </GroupedList>
      <GroupHead title="Your rooms" />
      {room ? (
        <GroupedList>
          <ListRow icon={room.live ? "live" : "play"} title={room.title} sub={`${room.published ? "" : "Draft. "}${when}`} to={room.published ? `/rooms/${room.id}` : boardPath(room.id)} />
        </GroupedList>
      ) : (
        <div className="k-card k-empty hs-empty">
          <b>No rooms yet</b>
          <Link className={makeIsNext ? "btn p" : "btn"} to={BOARD_NEW_PATH}>
            Make your first room
          </Link>
        </div>
      )}
      {next && !makeIsNext ? (
        phone ? (
          <DockedBar>
            <Link className="btn p full" to={next.to}>
              {next.label}
            </Link>
          </DockedBar>
        ) : (
          <div className="hs-start">
            <Link className="btn p" to={next.to}>
              {next.label}
            </Link>
          </div>
        )
      ) : null}
    </Page>
  );
}
